import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorInfo, TreeCompany } from '../../../../api/model';
import { usePostOrganizationsUpdate } from '../../../../api/origanization/origanization';
import { useDefaultOptions } from '../../../../hooks/api';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import { Company } from './Company';
import { CompanyModal } from './CompanyModal';

type Props = {
  companies: TreeCompany[];
  refetch: () => void;
};
function OrganizationTree({ companies, refetch }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);
  const [isCompanyModal, setIsCompanyModal] = useState(false);
  const [okModal, setOkModal] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isConfirmReset, setIsConfirmReset] = useState(false);
  const [displayUpdateError, setDisplayUpdateError] = useState(false);

  const { control, getValues, reset, handleSubmit, setValue } = useForm<{ companies: TreeCompany[] }>({
    defaultValues: {
      companies,
    },
  });

  const { fields, append } = useFieldArray({ name: 'companies', control });
  const { mutateAsync, isSuccess, isLoading, isError, error } = usePostOrganizationsUpdate<
    AxiosError<ErrorInfo>,
    unknown
  >(options);
  const handleReset = useCallback(() => {
    reset();
    setIsConfirmReset(false);
  }, [reset]);

  const onSubmit = useCallback(() => {
    setIsConfirm(false);
    void handleSubmit(async (data: FieldValues) => {
      await mutateAsync({
        data,
      });
    })();
  }, [handleSubmit, mutateAsync]);

  useEffect(() => {
    if (isSuccess) {
      setOkModal(true);
      refetch();
    }
    if (isError) {
      setDisplayUpdateError(true);
    }
  }, [isSuccess, isError, refetch]);

  useEffect(() => {
    setValue('companies', companies);
  }, [companies, setValue]);

  return (
    <section>
      <form onSubmit={onSubmit}>
        <div className="ttlnav">
          <h3 className="trig">
            <span>{t('グループ情報')}</span>
          </h3>
          <ul>
            <li onClick={() => setIsCompanyModal(true)}>
              <i className="fa-solid fa-circle-plus" />
              {t('会社追加')}
            </li>
          </ul>
        </div>
        <CompanyModal
          open={isCompanyModal}
          onClose={() => setIsCompanyModal(false)}
          getValues={getValues}
          setValue={setValue}
          append={append}
        />

        <div className="alignment">
          {fields.map((company, i) => (
            <Company
              control={control}
              companyIndex={i}
              company={company}
              key={`company-${String(company.company_id)}`}
              getValues={getValues}
              setValue={setValue}
            />
          ))}
        </div>

        <div className="controlfr btnfr btnfr1 mb0 mt100">
          <div>
            <span className="btn2 btnbg-g mr10">
              <Link to={`/${language}/organizations`}>{t('キャンセル')}</Link>
            </span>{' '}
            <span>
              <input
                type="button"
                value={t('元に戻す').toString()}
                className="btn1 btnbg-g"
                onClick={() => setIsConfirmReset(true)}
              />
            </span>
            <span>
              <input
                type="button"
                value={t('登録/更新').toString()}
                className="btn1 lsp03"
                onClick={() => setIsConfirm(true)}
              />
            </span>
          </div>
        </div>
      </form>
      {isLoading && <SpinnerModal />}

      {isConfirm && (
        <ConfirmModal onClickOk={() => onSubmit()} onClickCancel={() => setIsConfirm(false)}>
          <div className="poptxt">
            <p>{t('更新します。よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
      {isConfirmReset && (
        <ConfirmModal onClickOk={() => handleReset()} onClickCancel={() => setIsConfirmReset(false)}>
          <div className="poptxt">
            <p>{t('初期表示に戻りますが、よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
      {okModal && (
        <OkModal
          onClickOk={() => {
            setOkModal(false);
          }}
        >
          {t('更新処理が完了しました')}
        </OkModal>
      )}
      {/* スピナーモーダル */}
      {isLoading && <SpinnerModal />}
      {/* 更新エラー */}
      {displayUpdateError && <ErrorModal error={error} onClickOk={() => setDisplayUpdateError(false)} />}
    </section>
  );
}
export { OrganizationTree };
